import React, { type ReactNode, useEffect, useRef } from 'react';

import { useSearchParams } from 'react-router-dom';

import { FilterProvider, useFilter } from '../FilterProvider';

import { convertSearchParamsToNftFilters, removeNftFiltersFromSearchParams } from './utilities';

export interface NftFilterProviderProps {
    children: ReactNode;
    syncWithUrl?: boolean;
}

function ProviderWrappedChild({ children, syncWithUrl = false }: NftFilterProviderProps) {
    const [filters, setFilters] = useFilter();
    const [searchParams, setSearchParams] = useSearchParams();

    const filtersTimestamp = useRef<number>(0);
    const searchParamsTimestamp = useRef<number>(0);

    useEffect(() => {
        if (syncWithUrl) {
            const initialFilters = convertSearchParamsToNftFilters(searchParams);

            if (initialFilters.size) {
                setFilters(initialFilters);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (syncWithUrl) {
            filtersTimestamp.current = Date.now();
        }
    }, [syncWithUrl, filters]);

    useEffect(() => {
        if (syncWithUrl) {
            searchParamsTimestamp.current = Date.now();
        }
    }, [syncWithUrl, searchParams]);

    useEffect(() => {
        if (syncWithUrl) {
            const filtersParams = filters.urlSearchParams;
            const identicalCompare = filtersParams.toString() === searchParams.toString();

            if (identicalCompare) {
                return;
            }

            // By comparing timestamps, we can determine when a browser back/forward
            // navigation has occured, and in which "direction" we need to update state:
            // Push `filters` changes to `searchParams` or `searchParams` to `filters`.

            if (filtersTimestamp.current <= searchParamsTimestamp.current) {
                const filtersFromParams = convertSearchParamsToNftFilters(searchParams);
                setFilters(filtersFromParams);
            } else {
                const revisedSearchParams = removeNftFiltersFromSearchParams({ searchParams, filters });
                const updatedSearchParams = new URLSearchParams({
                    ...Object.fromEntries(revisedSearchParams),
                    ...Object.fromEntries(filtersParams),
                });

                if (searchParams.toString() !== updatedSearchParams.toString()) {
                    // Avoid a redundant history entry by checking
                    // against identical param values.
                    setSearchParams(updatedSearchParams);
                }
            }
        }
    }, [syncWithUrl, filters, searchParams, setFilters, setSearchParams]);

    return <>{children}</>;
}

export function NftFilterProvider({ children, syncWithUrl = false }: NftFilterProviderProps) {
    return (
        <FilterProvider>
            <ProviderWrappedChild syncWithUrl={syncWithUrl}>{children}</ProviderWrappedChild>
        </FilterProvider>
    );
}

export const useNftFilter = useFilter;
